import * as React from 'react';
import classnames from 'classnames';
import styles from './styles.module.css';
import { collectAllFields, Field, FieldValue, FieldValues, Schema } from './schema';
import { buildInitialValues, dirtyKeys, formReducer } from './utils/reducer';
import { validate } from './utils/validate';
import SchemaSections from './SchemaSections';
import { ConfigLayer, DifferingKeyDetail } from './fields/FieldWrapper';
import { Panel } from '@ixicare/ui';
import { t } from '~i18n';
import routes from '~config/routes';
import { useBreadcrumbs } from '~components/DashboardWrapper/Breadcrumbs';
import PageHeader from './PageHeader';
import ChangeSummary from './ChangeSummary';
import { buildStagedChanges, changeCount } from './utils/stagedChanges';
import { configKeysForField, mapConfigToForm, mapFormToConfig } from './utils/configMappers';
import { canRunAction, GatingContext } from './utils/gating';
import { BeneathValues, beneathLayers, buildLayerDetail, buildSiteDetail, OwnLayer } from './utils/layerDetail';
import { buildSourceChips } from './utils/sourceChips';
import { buildFallbackValues } from './utils/fallbackValues';
import ActionBar, { BarAction } from '~components/ActionBar/ActionBar';
import { Download, RotateCcw, Save, Send, Undo2, type LucideIcon } from 'lucide-react';

export interface SettingsDiffering {
  byKey: Record<string, Record<string, FieldValue>>;
  macById: Record<string, string>;
}

export type ConfigScope =
  // One device: its own settings, and the only scope where device-model fields (phone_number)
  // mean anything.
  | { kind: 'device'; mac: string; imei?: string }
  // Several devices at once: seeded with their common settings, `differing` lists the rest.
  | { kind: 'bulk'; macs: string[]; differing: SettingsDiffering }
  // A whole site's defaults for one device type. No devices, so no MACs to describe it with.
  | {
      kind: 'site';
      // Header label and middle breadcrumb, e.g. "Yve's Residence — ixi Black".
      label: string;
      // Where the middle breadcrumb links to: the site's own page.
      href?: string;
      // Chips naming the device types this form writes to.
      deviceTypeLabels: string[];
      // Filename stem for the config export.
      exportName: string;
    };

export interface Props {
  schema: Schema;
  // Header title and optional subtitle, already translated. Each page supplies its own, since the
  // schema's subtitle only describes a device's configuration.
  title: string;
  subtitle?: string;
  // The config values to seed the form with: the device's settings, the settings common to a
  // selection, or the site's stored layer.
  configValues: FieldValues;
  isInstaller: boolean;
  gating: GatingContext;
  onSubmit: (payload: FieldValues) => Promise<unknown>;
  scope: ConfigScope;
  // Standing consequence of the last thing the caller did, shown in the action bar -- site mode
  // uses it to say the values are stored but have or have not reached the devices. The form hides
  // it again the moment the values on screen stop matching what it describes, so the caller only
  // has to say what happened, not when to stop saying it.
  notice?: React.ReactNode;
  // Runs the schema's `send_to_devices` action. Required wherever the schema offers it, which is
  // the site form.
  onSendToDevices?: () => void;
  // Clears the device's own configuration level. Present only on the device form, and what makes
  // `reset` there a write to the service rather than the local revert it stays everywhere else.
  onResetOverrides?: () => Promise<unknown>;
  /**
   * The configuration layers this form can see, by layer.
   * **Memoise this in the caller.** The layer derivations key off the object, so a fresh literal
   * every render rebuilds them and re-renders every section with them.
   */
  configLayers?: Partial<Record<ConfigLayer, FieldValues>>;
  // Which layer this form writes. Absent on the bulk form, which writes none and can therefore say
  // only that the selection diverges from the site.
  ownLayer?: OwnLayer;
  // Drops the actions that would write. Separate from `gating` on purpose: gating says what the
  // user is, this says whether this particular form may be saved at all.
  readOnly?: boolean;
}

const MAX_CHIPS = 7;

const COLLAPSE_WIDTH = 1150;

// Actions that change the form's values, persist them, or act on them; the rest (export, for
// instance) stay available to a reader.
const WRITING_ACTIONS = new Set(['submit', 'reset', 'send_to_devices']);

// The bar drops to icon-only buttons once it is too narrow for labels, this provides icons for all actions.
const ACTION_ICONS: Record<string, LucideIcon> = {
  submit: Save,
  reset: RotateCcw,
  discard_changes: Undo2,
  download_config: Download,
  send_to_devices: Send,
};

const DeviceConfigForm: React.FC<Props> = ({
  schema,
  title,
  subtitle,
  configValues,
  isInstaller,
  gating,
  onSubmit,
  scope,
  notice,
  onSendToDevices,
  onResetOverrides,
  configLayers,
  ownLayer,
  readOnly,
}) => {
  const [state, dispatch] = React.useReducer(formReducer, undefined, () => {
    const schemaSeed = isInstaller ? mapConfigToForm(configValues) : configValues;
    const initial = buildInitialValues(schema, schemaSeed);
    return { values: initial, initialValues: initial, errors: {}, clearedKeys: [], pinnedKeys: [] };
  });

  // The layer this form writes. The device form and the site form say the same thing one layer
  // apart, so one derivation serves both.
  const ownLayerValues = ownLayer ? configLayers?.[ownLayer] : undefined;

  // Everything beneath it, which is the stack a value falls through to.
  const beneathValues = React.useMemo<BeneathValues>(
    () => (ownLayer ? Object.fromEntries(beneathLayers(ownLayer).map((layer) => [layer, configLayers?.[layer]])) : {}),
    [ownLayer, configLayers]
  );

  const fallbackFormValues = React.useMemo(
    () => buildFallbackValues(schema, isInstaller, ownLayer, beneathValues),
    [schema, isInstaller, ownLayer, beneathValues]
  );

  // Measured off the form rather than the viewport: the sidebar collapses to a rail or a drawer by
  // the user's choice, so the same viewport can leave the form anywhere from ~500px to full width.
  const formRef = React.useRef<HTMLDivElement>(null);
  const [narrow, setNarrow] = React.useState(false);

  React.useLayoutEffect(() => {
    const node = formRef.current;
    if (!node) return;
    // Measured before paint, so the bar does not render labelled and then snap to icons.
    const observer = new ResizeObserver(([entry]) => setNarrow(entry.contentRect.width <= COLLAPSE_WIDTH));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const differing = scope.kind === 'bulk' ? scope.differing : undefined;
  // Device-model fields (phone_number) belong to one physical device, so they only make sense
  // when exactly one is being configured.
  const hideDeviceModelLevel = scope.kind !== 'device';

  const dirty = React.useMemo(() => dirtyKeys(state.values, state.initialValues), [state.values, state.initialValues]);

  const fieldById = React.useMemo(() => {
    const m = new Map<string, Field>();
    for (const f of collectAllFields(schema)) m.set(f.id, f);
    return m;
  }, [schema]);

  // Only count visible changes as dirty (sub profile preset might change props
  // that aren't represented in the form). Map them to their translated labels
  // so the header tooltip can list which fields changed.
  const stagedIds = React.useMemo(() => {
    const staged = [...state.clearedKeys, ...state.pinnedKeys];
    if (!staged.length) return [];
    return [...fieldById.values()]
      .filter((field) => configKeysForField(field).some((key) => staged.includes(key)))
      .map((field) => field.id);
  }, [state.clearedKeys, state.pinnedKeys, fieldById]);

  const errors = React.useMemo(
    () => ({ ...validate(schema, state.values, new Set([...dirty, ...stagedIds])), ...state.errors }),
    [schema, state.values, state.errors, dirty, stagedIds]
  );

  const layerDetail = React.useMemo(() => {
    if (!ownLayerValues || !ownLayer) return undefined;
    return buildLayerDetail({
      schema,
      ownLayer,
      ownLayerValues,
      beneathValues,
      configValues,
      clearedKeys: state.clearedKeys,
      pinnedKeys: state.pinnedKeys,
    });
  }, [ownLayerValues, ownLayer, schema, beneathValues, configValues, state.clearedKeys, state.pinnedKeys]);

  const siteDetail = React.useMemo(() => {
    // The site, specifically -- not whichever layer happens to be beneath. This is the bulk form's
    // one statement: the selection diverges from the site.
    const siteValues = configLayers?.site;
    if (!siteValues || ownLayer) return undefined;
    return buildSiteDetail(schema, siteValues, state.values);
  }, [schema, state.values, configLayers, ownLayer]);

  // Only one of the three is ever defined, since each is gated on a prop only its own form passes.
  const inheritedDetail = layerDetail ?? siteDetail;

  // An unmarked field means "the device type's default" only where the form has its own layer to
  // compare against, and every layer between them was readable. A form with none beneath its own
  // has nothing to wait for.
  const layersResolved =
    Boolean(ownLayer) && beneathLayers(ownLayer!).every((layer) => beneathValues[layer] !== undefined);
  // Staging writes, so it follows `readOnly`. It also needs every layer beneath resolved, or the
  // value a cleared field falls back to would be a guess.
  const canStage = Boolean(layerDetail) && layersResolved && !readOnly;

  // Pressing the control moves the field across its layer's boundary, in whichever direction it is
  // currently on the wrong side of. Staged either way, so it is the payload that changes, not the
  // service, and `Discard changes` still reaches it.
  const handleToggleStored = React.useCallback(
    (field: Field) => {
      if (!ownLayerValues) return;
      const keys = configKeysForField(field);
      const stored = keys.filter((key) => ownLayerValues[key] !== undefined);
      if (stored.length) {
        const cleared = stored.every((key) => state.clearedKeys.includes(key));
        if (cleared) dispatch({ type: 'restoreField', id: field.id, keys: stored });
        else dispatch({ type: 'clearField', id: field.id, keys: stored, value: fallbackFormValues[field.id] });
        return;
      }
      const pinned = keys.some((key) => state.pinnedKeys.includes(key));
      if (pinned) {
        dispatch({ type: 'restoreField', id: field.id, keys });
        return;
      }
      // Drifted: the collated value is stale, so the fix is to make the service recollate rather
      // than to write anything. A null for a key the device level does not hold merges to nothing,
      // but the write still stamps `device_level_config_updated` and the save emits `trigger_ota`,
      // which recollates from the defaults, the site and the plane unconditionally. So the device
      // ends up inheriting properly rather than carrying a pinned copy of what it should inherit.
      dispatch({ type: 'clearField', id: field.id, keys, value: fallbackFormValues[field.id] });
    },
    [ownLayerValues, state.clearedKeys, state.pinnedKeys, fallbackFormValues]
  );

  const fieldRefs = React.useCallback(
    (ids: string[]) => ids.map((id) => ({ id, label: t(fieldById.get(id)?.title ?? id) })),
    [fieldById]
  );

  // What an unmarked field means. Withheld where a layer could not be read, since the legend would
  // then be calling a value a default when it might be an unread layer.
  const sourceLegendKey = layersResolved ? 'device-config.source-legend' : undefined;

  const sourceChips = React.useMemo(
    () => buildSourceChips({ layerDetail, siteDetail, ownLayer, layersResolved, fieldRefs }),
    [layerDetail, siteDetail, ownLayer, layersResolved, fieldRefs]
  );

  const differingDetail = React.useMemo(() => {
    if (!differing) return undefined;
    const { byKey, macById } = differing;
    const map = new Map<string, DifferingKeyDetail[]>();
    for (const f of collectAllFields(schema)) {
      const keys = configKeysForField(f).filter((k) => byKey[k]);
      if (!keys.length) continue;
      map.set(
        f.id,
        keys.map((key) => ({
          key,
          values: Object.entries(byKey[key]).map(([deviceId, value]) => ({
            mac: macById[deviceId] ?? deviceId,
            value,
          })),
        }))
      );
    }
    return map;
  }, [schema, differing]);

  const differingItems = React.useMemo(
    () =>
      differingDetail
        ? [...differingDetail.keys()].map((id) => ({ id, label: t(fieldById.get(id)?.title ?? id) }))
        : [],
    [differingDetail, fieldById]
  );

  const [profile, setProfile] = React.useState<string>(schema.meta.profile?.selected ?? 'custom');

  const [collapsedSections, setCollapsedSections] = React.useState<Record<string, boolean>>(() =>
    Object.fromEntries(Object.entries(schema.sections).map(([id, s]) => [id, Boolean(s.collapsed)]))
  );

  const toggleSection = React.useCallback((id: string) => {
    setCollapsedSections((c) => ({ ...c, [id]: !c[id] }));
  }, []);

  const [pendingScrollId, setPendingScrollId] = React.useState<string>();

  const revealField = React.useCallback(
    (id: string) => {
      const sectionRecord = Object.entries(schema.sections).find(([, s]) => s.fields.some((f) => f.id === id));
      const sId = sectionRecord?.[0];
      if (sId) setCollapsedSections((c) => (c[sId] ? { ...c, [sId]: false } : c));
      setPendingScrollId(id);
    },
    [schema]
  );

  React.useEffect(() => {
    if (!pendingScrollId) return;
    const raf = requestAnimationFrame(() => {
      document.getElementById(pendingScrollId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setPendingScrollId(undefined);
    });
    return () => cancelAnimationFrame(raf);
  }, [pendingScrollId, collapsedSections]);

  const handleChange = React.useCallback(
    (id: string, value: FieldValue) => {
      // The keys this control writes, so the reducer can stage without knowing the schema.
      // `applySubProfile` calls this once per key, which is why it cannot assume `[id]`.
      const field = fieldById.get(id);
      const keys = field ? configKeysForField(field) : [id];
      // Editing a field the layer does not hold is how it joins: the value alone cannot ask for
      // that, since a field already shows the default it would be stored as.
      const pin = canStage && !keys.some((key) => ownLayerValues?.[key] !== undefined);
      dispatch({ type: 'setValue', id, value, keys, pin });
    },
    [fieldById, canStage, ownLayerValues]
  );

  const handleSubmit = React.useCallback(() => {
    if (Object.keys(errors).length > 0) return;

    const payload: FieldValues = {};
    if (isInstaller) {
      // Synthetic fields explode into several config keys, so diff at the
      // config level (not the form level) to send only the changed params.
      const allFields = collectAllFields(schema);
      const current = mapFormToConfig(state.values, allFields);
      const initial = mapFormToConfig(state.initialValues, allFields);
      for (const k of new Set([...Object.keys(current), ...Object.keys(initial)])) {
        if (current[k] !== initial[k]) payload[k] = current[k];
      }
    } else {
      for (const k of dirty) payload[k] = state.values[k];
    }

    // Membership of the layer, which the value diff cannot express. A cleared key leaves it; a
    // pinned one joins it at whatever the field shows, even where that equals the type default and
    // so produced no diff at all.
    const current = mapFormToConfig(state.values, collectAllFields(schema));
    for (const key of state.pinnedKeys) {
      const value = key in current ? current[key] : state.values[key];
      // A null would read as the opposite instruction at the service, which strips null to delete
      // the key. Nothing to store is nothing to send.
      if (value !== null && value !== undefined) payload[key] = value;
    }
    for (const key of state.clearedKeys) payload[key] = null;

    onSubmit(payload)
      .then(() => {
        dispatch({ type: 'commit', values: state.values });
      })
      .catch(() => {
        /* notification handled in store */
      });
  }, [
    errors,
    state.values,
    state.initialValues,
    state.pinnedKeys,
    state.clearedKeys,
    dirty,
    isInstaller,
    onSubmit,
    schema,
  ]);

  const handleReset = React.useCallback(() => {
    dispatch({ type: 'reset' });
  }, []);

  // Where the device's own level can be cleared, `reset` stops being a local revert and becomes a
  // write. Every layer beneath has to be readable for that: they are what the cleared fields fall
  // back to, and without them the form would redraw them as bare type defaults and misreport what
  // the device now runs. Where one cannot be read, `reset` keeps its local-revert meaning.
  //
  // Only the device form can clear its own level, so the count is of that layer and no other.
  const overrideCount = ownLayer === 'device' && ownLayerValues ? Object.keys(ownLayerValues).length : 0;
  const clearsOverrides = Boolean(onResetOverrides && ownLayer === 'device' && ownLayerValues && layersResolved);

  const handleClearOverrides = React.useCallback(() => {
    if (!onResetOverrides) return;
    onResetOverrides()
      .then(() => {
        // Nothing beneath the device level was written, so what it runs now is every layer under it
        // over the type defaults -- which is exactly what a cleared field falls back to, already
        // derived. Seeding from the site alone would show site values where the device is in fact
        // running the resident's on top of them. Beats waiting for the service to recollate.
        //
        // Device model attributes (phone_number) are not configuration and no layer resolves them,
        // so they are carried across rather than rebuilt.
        const next = { ...fallbackFormValues };
        for (const field of collectAllFields(schema)) {
          if (field.device_model_level) next[field.id] = state.values[field.id];
        }
        dispatch({ type: 'init', values: next });
      })
      .catch(() => {
        /* notification handled in store */
      });
  }, [onResetOverrides, fallbackFormValues, schema, state.values]);

  const handleDownload = React.useCallback(() => {
    const blob = new Blob([JSON.stringify(state.values, null, 2)], {
      type: 'application/json',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const downloadName = scope.kind === 'site' ? scope.exportName : scope.kind === 'device' ? scope.mac : 'bulk';
    a.download = `device-config-${downloadName}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [state.values, scope]);

  // What a save would do, for the dialog that asks first. Derived from the same state the payload
  // is built from, so the two cannot disagree.
  const stagedChanges = React.useMemo(
    () =>
      buildStagedChanges({
        fields: fieldById,
        values: state.values,
        initialValues: state.initialValues,
        dirty,
        clearedKeys: state.clearedKeys,
        pinnedKeys: state.pinnedKeys,
        fallbackValues: fallbackFormValues,
      }),
    [fieldById, state.values, state.initialValues, dirty, state.clearedKeys, state.pinnedKeys, fallbackFormValues]
  );

  // What clearing the device's own level would do. The whole layer is on its way out, so it is the
  // staged-clear list with every stored key in it -- one derivation, so the two dialogs cannot
  // describe the same removal differently.
  const resetChanges = React.useMemo(
    () =>
      clearsOverrides
        ? buildStagedChanges({
            fields: fieldById,
            values: state.values,
            initialValues: state.initialValues,
            // Nothing is edited or joining the layer: this action only removes.
            dirty: [],
            clearedKeys: Object.keys(ownLayerValues ?? {}),
            pinnedKeys: [],
            fallbackValues: fallbackFormValues,
          })
        : undefined,
    [clearsOverrides, ownLayerValues, fieldById, state.values, state.initialValues, fallbackFormValues]
  );

  // Who the save reaches. A count rather than the MACs on the bulk form: the dialog is there to be
  // read, and twenty addresses are not.
  const appliesTo =
    scope.kind === 'site'
      ? scope.deviceTypeLabels
      : scope.kind === 'device'
        ? [scope.mac]
        : [t('device-config.bulkDevices', { count: scope.macs.length })];

  // Adapt the schema's actions into the generic ActionBar contract. Submit /
  // reset / download dispatch is keyed off the action id, as it was before.
  const hasErrors = Object.keys(errors).length > 0;
  const barActions: BarAction[] = schema.actions
    .filter((a) => (a.placement ?? 'actionbar') === 'actionbar' && canRunAction(a, gating, scope.kind))
    .filter((a) => !readOnly || !WRITING_ACTIONS.has(a.id))
    .map((a) => ({
      id: a.id,
      label: t(a.label_key),
      variant: a.variant,
      lucide: ACTION_ICONS[a.id],
      disabled:
        (a.id === 'submit' && ((dirty.length === 0 && stagedIds.length === 0) || hasErrors)) ||
        // Nothing to clear is not the same as nothing to revert: a device on the site's values
        // already is where reset would put it.
        (a.id === 'reset' && clearsOverrides && overrideCount === 0),
      confirm: a.confirm && {
        title: t(a.confirm.title_key),
        body: t(a.confirm.body_key, { count: overrideCount }),
        ...(a.id === 'submit' && {
          details: <ChangeSummary changes={stagedChanges} appliesTo={appliesTo} />,
          action: t('device-config.actions.submit-confirm.action'),
        }),
        // Clearing the device's level writes as much as a save does, so it is owed the same list.
        // The other `reset` only reverts the form, where a list would be naming what is about to be
        // thrown away -- the opposite meaning under the same heading.
        ...(a.id === 'reset' &&
          resetChanges && {
            details: (
              <ChangeSummary
                changes={resetChanges}
                appliesTo={appliesTo}
                // The write is followed by a re-seed from the site's values, so whatever is staged
                // or edited goes with it. Saving first is the way to keep it.
                discarding={changeCount(stagedChanges)}
              />
            ),
          }),
      },
      onClick: () => {
        if (a.id === 'submit') handleSubmit();
        else if (a.id === 'reset') (clearsOverrides ? handleClearOverrides : handleReset)();
        else if (a.id === 'download_config') handleDownload();
        else if (a.id === 'send_to_devices') onSendToDevices?.();
      },
    }));

  // Synthesised reset form rather than declared in the schema, because its existence is a fact about this
  // form and not about the device type.
  if (clearsOverrides && !readOnly) {
    const resetAt = barActions.findIndex((a) => a.id === 'reset');
    barActions.splice(resetAt < 0 ? barActions.length : resetAt, 0, {
      id: 'discard_changes',
      label: t('device-config.actions.discard'),
      lucide: ACTION_ICONS.discard_changes,
      variant: 'danger',
      disabled: dirty.length === 0 && stagedIds.length === 0,
      onClick: handleReset,
    });
  }

  const headerLabel =
    scope.kind === 'site'
      ? scope.label
      : scope.kind === 'device'
        ? scope.mac + (scope.imei ? ` || ${'IMEI: ' + scope.imei}` : '')
        : t('device-config.bulkDevices', { count: scope.macs.length });

  // The devices crumb keeps its own wording but now links, which is what the layout derives the
  // back link from — the hand-rolled one pointed at the same place.
  useBreadcrumbs([
    { label: t('device-config.crumbs.devices'), href: routes.deviceOverview },
    {
      label: headerLabel,
      href:
        scope.kind === 'site'
          ? scope.href
          : scope.kind === 'device'
            ? `${routes.deviceOverview}?q=${scope.mac}#deviceFinder`
            : undefined,
    },
    {
      label: scope.kind === 'site' ? t('device-config.crumbs.siteConfig') : t('device-config.crumbs.config'),
    },
  ]);

  // Cap the chip list so a large bulk selection doesn't overflow the bar.
  const chips =
    scope.kind === 'site'
      ? scope.deviceTypeLabels
      : scope.kind === 'device'
        ? [scope.mac]
        : scope.macs.length <= MAX_CHIPS
          ? scope.macs
          : [...scope.macs.slice(0, MAX_CHIPS), `+${scope.macs.length - MAX_CHIPS}`];

  return (
    <>
      <div ref={formRef} className={classnames(styles.configForm, narrow && styles.narrow)}>
        <Panel>
          <PageHeader
            schema={schema}
            title={title}
            subtitle={subtitle}
            headerLabel={headerLabel}
            sourceChips={sourceChips}
            sourceLegendKey={sourceLegendKey}
            differingItems={differingItems}
            profile={profile}
            gating={gating}
            scope={scope.kind}
            onProfileChange={setProfile}
            onExportClick={handleDownload}
            onRevealField={revealField}
            collapsedSections={collapsedSections}
            setCollapsedSections={setCollapsedSections}
          />
          <SchemaSections
            schema={schema}
            values={state.values}
            initialValues={state.initialValues}
            errors={errors}
            gating={gating}
            collapsed={collapsedSections}
            onToggleCollapsed={toggleSection}
            onChange={handleChange}
            differingDetail={differingDetail}
            inheritedDetail={inheritedDetail}
            hideDeviceModelLevel={hideDeviceModelLevel}
            dimInherited={ownLayer === 'site'}
            onToggleStored={canStage ? handleToggleStored : undefined}
            readOnly={readOnly}
          />
        </Panel>
      </div>
      {/* Outside the form: it is pinned to the viewport, not part of the panel's content, and
          keeping it out means a future `contain` or `transform` on the form cannot unpin it. */}
      <ActionBar
        title={t(scope.kind === 'site' ? 'device-config.actionbar.appliesTo' : 'device-config.actionbar.selected')}
        chips={chips}
        notice={dirty.length > 0 || stagedIds.length > 0 ? undefined : notice}
        actions={barActions}
        actionStyle={narrow ? 'icon' : 'button'}
      />
    </>
  );
};

export default DeviceConfigForm;
